import { useState, useEffect, useRef } from 'react';

interface FeatureVideoCardProps {
  className?: string;
}

export default function FeatureVideoCard({ className = '' }: FeatureVideoCardProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoStartTime = useRef<number>(0);

  // Handle scroll-based rotation
  useEffect(() => {
    const handleScroll = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const cardCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;
        const distanceFromCenter = cardCenter - viewportCenter;
        const maxDistance = windowHeight / 2;
        const progress = Math.max(0, Math.min(1, 1 - (distanceFromCenter / maxDistance)));
        setScrollProgress(progress);

        if (progress > 0.75 && !isVideoPlaying) {
          setIsVideoPlaying(true);
          setShowVideo(true);
          videoStartTime.current = Date.now();
          if (audioRef.current && !isMuted) {
            audioRef.current.volume = 0.25;
            audioRef.current.play().catch(() => {});
          }
        }

        if (progress < 0.2 && isVideoPlaying) {
          setIsVideoPlaying(false);
          setShowVideo(false);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVideoPlaying, isMuted]);

  // Scene progression for 45-second video
  useEffect(() => {
    if (!isVideoPlaying) {
      setCurrentScene(0);
      return;
    }

    const sceneTimings = [0, 5000, 15000, 25000, 32000, 40000];

    const updateScene = () => {
      const elapsed = Date.now() - videoStartTime.current;

      if (elapsed >= 45000) {
        videoStartTime.current = Date.now();
        setCurrentScene(0);
        return;
      }

      for (let i = sceneTimings.length - 1; i >= 0; i--) {
        if (elapsed >= sceneTimings[i]) {
          setCurrentScene(i);
          break;
        }
      }
    };

    const interval = setInterval(updateScene, 100);
    return () => clearInterval(interval);
  }, [isVideoPlaying]);

  const rotateX = 50 * (1 - scrollProgress);
  const translateY = 60 * (1 - scrollProgress);
  const scale = 0.85 + (0.15 * scrollProgress);

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
      if (!newMuted && isVideoPlaying) {
        audioRef.current.volume = 0.25;
        audioRef.current.play().catch(() => {});
      }
    }
  };

  return (
    <div className={`relative ${className}`} style={{ perspective: '1500px' }}>
      <audio
        ref={audioRef}
        loop
        muted={isMuted}
        src="https://assets.mixkit.co/music/preview/mixkit-tech-house-vibes-130.mp3"
      />

      {/* 3D Card Container */}
      <div
        ref={cardRef}
        className="relative mx-auto w-full max-w-4xl"
        style={{
          transform: `rotateX(${rotateX}deg) translateY(${translateY}px) scale(${scale})`,
          transformStyle: 'preserve-3d',
          transition: 'transform 0.4s ease-out',
        }}
      >
        {/* Card Frame - Device style */}
        <div
          className="relative rounded-[24px] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 50%, #020617 100%)',
            padding: '16px',
            boxShadow: `
              0 60px 120px -30px rgba(0, 0, 0, 0.6),
              0 40px 80px -40px rgba(0, 0, 0, 0.5),
              inset 0 1px 0 rgba(255, 255, 255, 0.1),
              inset 0 -1px 0 rgba(0, 0, 0, 0.3)
            `,
          }}
        >
          {/* Screen */}
          <div className="relative rounded-[16px] overflow-hidden bg-black aspect-video">
            {/* Video Content */}
            <div className="absolute inset-0">
              {showVideo ? (
                <ProfessionalVideoContent currentScene={currentScene} />
              ) : (
                <VideoPlaceholder />
              )}
            </div>

            {/* Screen Reflection */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 40%)',
              }}
            />

            {/* Sound Toggle */}
            {showVideo && (
              <button
                onClick={toggleMute}
                className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-all z-20 border border-white/10"
              >
                {isMuted ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
              </button>
            )}

            {/* Progress Bar */}
            {showVideo && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"
                  style={{
                    width: `${((currentScene + 1) / 6) * 100}%`,
                    transition: 'width 0.5s ease-out',
                  }}
                />
              </div>
            )}
          </div>

          {/* Bottom bar - laptop style */}
          <div className="h-4 mt-3 rounded-full bg-gradient-to-b from-gray-700 to-gray-800 flex items-center justify-center">
            <div className="w-20 h-1.5 rounded-full bg-gray-600" />
          </div>
        </div>

        {/* Reflection glow */}
        <div
          className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-2/3 h-24 rounded-full"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
            filter: 'blur(25px)',
            transform: `scaleY(${0.2 + scrollProgress * 0.8})`,
            opacity: scrollProgress,
          }}
        />
      </div>
    </div>
  );
}

// Placeholder when video is not playing
function VideoPlaceholder() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900">
      <AnimatedBackground />
      <div className="relative z-10 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
          <svg className="w-10 h-10 text-white/70" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        <p className="text-white/50 text-sm">Scroll to play</p>
      </div>
    </div>
  );
}

// Animated gradient background with particles
function AnimatedBackground() {
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, size: number, speed: number}>>([]);

  useEffect(() => {
    // Generate initial particles
    const initialParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3,
      speed: 0.5 + Math.random() * 1,
    }));
    setParticles(initialParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Gradient mesh background */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 60%, rgba(236, 72, 153, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 50% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)
          `,
          animation: 'gradientShift 20s ease-in-out infinite',
        }}
      />

      {/* Floating particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-white/20"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animation: `floatUp ${10 / particle.speed}s linear infinite`,
            animationDelay: `${particle.id * 0.5}s`,
          }}
        />
      ))}

      <style>{`
        @keyframes gradientShift {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(2deg); }
        }
        @keyframes floatUp {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.3; }
          90% { opacity: 0.3; }
          100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Professional Video Content with cursor effects
function ProfessionalVideoContent({ currentScene }: { currentScene: number }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <AnimatedBackground />

      {/* Scene 0: Logo Intro */}
      <SceneWrapper isActive={currentScene === 0}>
        <LogoIntroScene />
      </SceneWrapper>

      {/* Scene 1: Dashboard Overview */}
      <SceneWrapper isActive={currentScene === 1}>
        <DashboardScene />
      </SceneWrapper>

      {/* Scene 2: AI Generation */}
      <SceneWrapper isActive={currentScene === 2}>
        <AIGenerationScene />
      </SceneWrapper>

      {/* Scene 3: Multi-Platform */}
      <SceneWrapper isActive={currentScene === 3}>
        <MultiPlatformScene />
      </SceneWrapper>

      {/* Scene 4: Analytics */}
      <SceneWrapper isActive={currentScene === 4}>
        <AnalyticsScene />
      </SceneWrapper>

      {/* Scene 5: CTA */}
      <SceneWrapper isActive={currentScene === 5}>
        <CTAScene />
      </SceneWrapper>
    </div>
  );
}

function SceneWrapper({ isActive, children }: { isActive: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`absolute inset-0 transition-all duration-700 ${
        isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
      }`}
    >
      {children}
    </div>
  );
}

// Scene 0: Logo Intro
function LogoIntroScene() {
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowText(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="animate-logo-in">
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-2xl">
          <span className="text-5xl font-bold text-white">S</span>
        </div>
      </div>
      <div className={`mt-6 transition-all duration-700 ${showText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h1 className="text-4xl font-bold text-white mb-2">Soail</h1>
        <p className="text-white/70 text-lg">AI-Powered Marketing</p>
      </div>

      <style>{`
        @keyframes logo-in {
          0% { transform: scale(0.5) rotate(-10deg); opacity: 0; }
          50% { transform: scale(1.1) rotate(2deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .animate-logo-in { animation: logo-in 0.8s ease-out; }
      `}</style>
    </div>
  );
}

// Scene 1: Dashboard with cursor
function DashboardScene() {
  const [cursorPos, setCursorPos] = useState({ x: 20, y: 20 });
  const [showRipple, setShowRipple] = useState(false);
  const [ripplePos, setRipplePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const positions = [
      { x: 20, y: 20 },
      { x: 50, y: 30 },
      { x: 70, y: 50 },
      { x: 55, y: 70 },
    ];
    let index = 0;

    const moveCursor = () => {
      setCursorPos(positions[index]);
      if (index === 3) {
        setTimeout(() => {
          setRipplePos({ x: 55, y: 70 });
          setShowRipple(true);
          setTimeout(() => setShowRipple(false), 600);
        }, 400);
      }
      index = (index + 1) % positions.length;
    };

    moveCursor();
    const interval = setInterval(moveCursor, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full h-full p-6 relative">
      {/* Browser mockup */}
      <div className="bg-gray-900/80 rounded-xl border border-white/10 h-full overflow-hidden">
        {/* Browser bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-800/50 border-b border-white/10">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <div className="flex-1 mx-4">
            <div className="bg-gray-700/50 rounded-lg px-3 py-1.5 text-xs text-gray-400">
              app.soail.com/dashboard
            </div>
          </div>
        </div>

        {/* Dashboard content */}
        <div className="p-4 flex gap-4 h-[calc(100%-48px)]">
          {/* Sidebar */}
          <div className="w-16 bg-gray-800/50 rounded-lg p-2 flex flex-col gap-2">
            {['📊', '📧', '📱', '⚡', '⚙️'].map((icon, i) => (
              <div key={i} className="w-full aspect-square rounded-lg bg-gray-700/50 flex items-center justify-center text-lg">
                {icon}
              </div>
            ))}
          </div>

          {/* Main content */}
          <div className="flex-1 space-y-4">
            <div className="flex gap-3">
              {['Videos', 'Ads', 'Social', 'Email'].map((tab, i) => (
                <div
                  key={tab}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    i === 0 ? 'bg-purple-500/30 text-purple-300' : 'bg-gray-700/30 text-gray-400'
                  }`}
                >
                  {tab}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-4 text-center">
              <p className="text-white font-semibold">Create New Campaign</p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Active', value: '24' },
                { label: 'Scheduled', value: '18' },
                { label: 'Drafts', value: '7' },
              ].map((stat) => (
                <div key={stat.label} className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Animated cursor */}
      <AnimatedCursor x={cursorPos.x} y={cursorPos.y} />

      {/* Click ripple */}
      {showRipple && <ClickRipple x={ripplePos.x} y={ripplePos.y} />}
    </div>
  );
}

// Scene 2: AI Generation
function AIGenerationScene() {
  const [text, setText] = useState('');
  const [progress, setProgress] = useState(0);
  const fullText = "🚀 Introducing our revolutionary new product! Transform your workflow with AI-powered automation. Join 10,000+ satisfied customers today!";

  useEffect(() => {
    let index = 0;
    const typeInterval = setInterval(() => {
      if (index <= fullText.length) {
        setText(fullText.slice(0, index));
        setProgress((index / fullText.length) * 100);
        index++;
      } else {
        clearInterval(typeInterval);
      }
    }, 40);

    return () => clearInterval(typeInterval);
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-gray-900/80 rounded-xl border border-white/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <span className="text-lg">🤖</span>
          </div>
          <div>
            <p className="text-white font-medium">AI is creating your content...</p>
            <p className="text-gray-400 text-sm">Generating social post</p>
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-lg p-4 mb-4 min-h-[100px]">
          <p className="text-white/90 leading-relaxed">
            {text}
            <span className="animate-pulse text-purple-400">|</span>
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Progress</span>
            <span className="text-purple-400">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Scene 3: Multi-Platform
function MultiPlatformScene() {
  const [connectedPlatforms, setConnectedPlatforms] = useState<number[]>([]);

  useEffect(() => {
    const platforms = [0, 1, 2, 3, 4];
    platforms.forEach((p, i) => {
      setTimeout(() => {
        setConnectedPlatforms(prev => [...prev, p]);
      }, i * 400);
    });
  }, []);

  const platforms = [
    { name: 'Instagram', color: '#E4405F', icon: '📷' },
    { name: 'Facebook', color: '#1877F2', icon: '📘' },
    { name: 'TikTok', color: '#000000', icon: '🎵' },
    { name: 'LinkedIn', color: '#0A66C2', icon: '💼' },
    { name: 'Twitter', color: '#1DA1F2', icon: '🐦' },
  ];

  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-8">One Dashboard, All Platforms</h2>

        <div className="flex items-center justify-center gap-4">
          {/* Content card */}
          <div className="w-32 bg-gray-800/80 rounded-xl p-3 border border-white/10">
            <div className="w-full aspect-square bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-lg mb-2" />
            <p className="text-xs text-white/70 truncate">Your content</p>
          </div>

          {/* Connection lines and platforms */}
          <div className="flex flex-col gap-2">
            {platforms.map((platform, i) => (
              <div
                key={platform.name}
                className={`flex items-center gap-2 transition-all duration-500 ${
                  connectedPlatforms.includes(i) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
              >
                <div className="w-12 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500" />
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                  style={{ backgroundColor: platform.color }}
                >
                  {platform.icon}
                </div>
                {connectedPlatforms.includes(i) && (
                  <span className="text-green-400 animate-bounce-in">✓</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white/60 text-sm">All platforms synced</span>
        </div>
      </div>

      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        .animate-bounce-in { animation: bounce-in 0.4s ease-out; }
      `}</style>
    </div>
  );
}

// Scene 4: Analytics
function AnalyticsScene() {
  const [counts, setCounts] = useState({ engagement: 0, reach: 0, growth: 0 });

  useEffect(() => {
    const targets = { engagement: 247, reach: 189, growth: 312 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const counter = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounts({
        engagement: Math.round(targets.engagement * progress),
        reach: Math.round(targets.reach * progress),
        growth: Math.round(targets.growth * progress),
      });
      if (step >= steps) clearInterval(counter);
    }, interval);

    return () => clearInterval(counter);
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <h2 className="text-xl font-bold text-white text-center mb-6">Performance Overview</h2>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Engagement', value: counts.engagement, icon: '📈' },
            { label: 'Reach', value: counts.reach, icon: '📊' },
            { label: 'Growth', value: counts.growth, icon: '🚀' },
          ].map((stat) => (
            <div key={stat.label} className="bg-gray-800/60 rounded-xl p-4 text-center border border-white/10">
              <span className="text-2xl">{stat.icon}</span>
              <div className="text-3xl font-bold text-white mt-2">+{stat.value}%</div>
              <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Mini chart */}
        <div className="bg-gray-800/60 rounded-xl p-4 border border-white/10">
          <div className="flex items-end justify-between h-20 gap-1">
            {[30, 45, 35, 60, 55, 75, 65, 85, 80, 95, 90, 100].map((h, i) => (
              <div
                key={i}
                className="flex-1 bg-gradient-to-t from-purple-500 to-pink-500 rounded-t animate-chart-grow"
                style={{
                  height: `${h}%`,
                  animationDelay: `${i * 100}ms`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes chart-grow {
          0% { transform: scaleY(0); }
          100% { transform: scaleY(1); }
        }
        .animate-chart-grow {
          animation: chart-grow 0.5s ease-out forwards;
          transform-origin: bottom;
        }
      `}</style>
    </div>
  );
}

// Scene 5: CTA
function CTAScene() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div className="text-center animate-slide-in-left">
          <div className="text-5xl font-bold text-white">10x</div>
          <p className="text-white/70 mt-1">More Content</p>
        </div>
        <div className="text-center animate-slide-in-right">
          <div className="text-5xl font-bold text-white">75%</div>
          <p className="text-white/70 mt-1">Faster Creation</p>
        </div>
      </div>

      <button className="px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-bold rounded-full text-lg animate-pulse-glow">
        Get Started Free →
      </button>

      <style>{`
        @keyframes slide-in-left {
          0% { transform: translateX(-30px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-in-right {
          0% { transform: translateX(30px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.5); }
          50% { box-shadow: 0 0 40px rgba(168, 85, 247, 0.8); }
        }
        .animate-slide-in-left { animation: slide-in-left 0.6s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.6s ease-out 0.2s both; }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Animated Cursor component
function AnimatedCursor({ x, y }: { x: number; y: number }) {
  return (
    <div
      className="absolute pointer-events-none z-30 transition-all duration-500 ease-out"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {/* Cursor trail */}
      <div
        className="absolute w-6 h-6 rounded-full bg-purple-500/30 blur-sm"
        style={{ transform: 'translate(-50%, -50%)' }}
      />
      {/* Cursor */}
      <svg width="24" height="24" viewBox="0 0 24 24" className="drop-shadow-lg">
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.48 0 .72-.58.38-.92L6.35 2.85a.5.5 0 0 0-.85.36z"
          fill="white"
          stroke="#7c3aed"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}

// Click Ripple effect
function ClickRipple({ x, y }: { x: number; y: number }) {
  return (
    <div
      className="absolute pointer-events-none z-20"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <div className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 animate-ripple">
        <div className="absolute inset-0 rounded-full border-2 border-purple-500" />
      </div>

      <style>{`
        @keyframes ripple {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(4); opacity: 0; }
        }
        .animate-ripple { animation: ripple 0.6s ease-out; }
      `}</style>
    </div>
  );
}
